// Tenantra Inc. - Public launch waitlist (Facebook ad destination).
import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, ShieldCheck, Bell, Rocket } from 'lucide-react';
import { Logo } from '@/components/Logo';
import api, { apiHelpers } from '@/services/api';

const PROVINCES = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'], ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
];

const HERO = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYW5hZGlhbiUyMGFwYXJ0bWVudCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkzMjQ1NjA2fDA&ixlib=rb-4.1.0&q=85';

export default function WaitlistPage() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', role_type: 'owner', province: '', units: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2) { toast.error('Please enter your full name'); return; }
    if (!form.email.trim()) { toast.error('Please enter your email'); return; }
    setBusy(true);
    try {
      await api.post('/waitlist', {
        full_name: form.full_name.trim(), email: form.email.trim(),
        phone: form.phone.trim() || undefined, role_type: form.role_type,
        province: form.province || undefined, units: form.units.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white grid lg:grid-cols-2">
      {/* Left / brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/90 via-stone-950/70 to-teal-950/80" />
        <div className="relative z-10"><Logo size="lg" dark /></div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/15 border border-teal-400/30 text-teal-300 text-xs font-semibold tracking-wide mb-5">
            <Rocket className="w-3.5 h-3.5" /> EARLY ACCESS
          </div>
          <h2 className="text-3xl font-bold leading-tight mb-4">Canada's rental credit bureau is almost here.</h2>
          <p className="text-stone-300 text-sm leading-relaxed mb-8">Join the waitlist and be among the first landlords to report rent, screen tenants, and build verified rental histories the moment we launch.</p>
          <ul className="space-y-3 text-sm text-stone-200">
            <li className="flex items-start gap-3"><Bell className="w-4 h-4 text-teal-400 mt-0.5" /> Be notified the second we go live</li>
            <li className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 text-teal-400 mt-0.5" /> One-click account activation — no re-typing</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5" /> Priority onboarding for founding members</li>
          </ul>
        </div>
        <div className="relative z-10 text-xs text-stone-400">&copy; {new Date().getFullYear()} Tenantra Inc. · Canada</div>
      </div>

      {/* Right / form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white text-slate-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size="md" /></div>
          {done ? (
            <div className="text-center py-10" data-testid="waitlist-success">
              <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-teal-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">You're on the list! 🎉</h1>
              <p className="text-slate-500 text-sm leading-relaxed">Thanks for joining, {form.full_name.split(' ')[0]}. We'll email <span className="font-medium text-slate-700">{form.email}</span> the moment Tenantra launches — with a link that activates your account in one click.</p>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-semibold mb-4">
                <Rocket className="w-3.5 h-3.5" /> Join the waitlist
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1.5">Reserve your spot</h1>
              <p className="text-slate-500 text-sm mb-6">Takes 20 seconds. We'll only use this to tell you when we launch.</p>
              <form onSubmit={submit} className="space-y-3.5">
                <Field label="Full name" required>
                  <input value={form.full_name} onChange={set('full_name')} placeholder="Jane Doe" autoComplete="name" className={inputCls} data-testid="waitlist-name" />
                </Field>
                <Field label="Email" required>
                  <input value={form.email} onChange={set('email')} type="email" placeholder="jane@example.com" autoComplete="email" className={inputCls} data-testid="waitlist-email" />
                </Field>
                <Field label="Phone (optional)">
                  <input value={form.phone} onChange={set('phone')} type="tel" placeholder="+1 416 555 0123" autoComplete="tel" className={inputCls} data-testid="waitlist-phone" />
                </Field>
                <Field label="I am a…" required>
                  <select value={form.role_type} onChange={set('role_type')} className={inputCls} data-testid="waitlist-role">
                    <option value="owner">Landlord / Property Owner</option>
                    <option value="property_manager">Property Manager</option>
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Province (optional)">
                    <select value={form.province} onChange={set('province')} className={inputCls} data-testid="waitlist-province">
                      <option value="">Select…</option>
                      {PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                    </select>
                  </Field>
                  <Field label="Rental units (optional)">
                    <input value={form.units} onChange={set('units')} placeholder="e.g. 5" className={inputCls} data-testid="waitlist-units" />
                  </Field>
                </div>
                <button disabled={busy} className="w-full h-11 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors" data-testid="waitlist-submit">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}Join the waitlist
                </button>
                <p className="text-[11px] text-slate-400 text-center leading-relaxed">By joining you agree to receive a launch notification from Tenantra. No spam, unsubscribe anytime.</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 mb-1 block">{label}{required && <span className="text-teal-600"> *</span>}</span>
      {children}
    </label>
  );
}
